import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { ErrorState, SkeletonRows } from '@/components/admin/states';
import type {
  ChannelPrefs,
  ChannelPrefState,
  ChannelPrefUpdateInput,
  ExternalChannel,
} from '@/features/notifications/api';
import { ApiError } from '@/lib/api/http';

/**
 * Slack・Chatworkの宛先登録（2026-08-18 先方回答 / D-106）。
 * 通知は必ずメールとアプリ内に届き、ここに登録した宛先には「追加で」届く。
 * 宛先の値は再表示しない（登録済みかどうかと末尾のヒントのみ / D-067と同じ方針）。
 */
export function ChannelPrefsBox({
  queryKey,
  fetchPrefs,
  updatePref,
  officeMode = false,
}: {
  queryKey: readonly unknown[];
  fetchPrefs: () => Promise<ChannelPrefs>;
  updatePref: (input: ChannelPrefUpdateInput) => Promise<ChannelPrefs>;
  /** 事務局による代理登録画面かどうか（説明文が変わる） */
  officeMode?: boolean;
}): ReactElement {
  const queryClient = useQueryClient();
  const [slackDraft, setSlackDraft] = useState('');
  const [chatworkDraft, setChatworkDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({ queryKey, queryFn: fetchPrefs });

  const mutation = useMutation({
    mutationFn: updatePref,
    onSuccess: async () => {
      setError(null);
      setSlackDraft('');
      setChatworkDraft('');
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '操作に失敗しました。');
    },
  });

  if (query.isPending) return <SkeletonRows rows={2} />;
  if (query.isError) {
    return (
      <ErrorState
        message="通知の宛先の取得に失敗しました。"
        onRetry={() => void query.refetch()}
      />
    );
  }

  const prefs = query.data;
  const busy = mutation.isPending;

  function renderChannel(
    channel: ExternalChannel,
    label: string,
    state: ChannelPrefState,
    draft: string,
    setDraft: (v: string) => void,
    placeholder: string,
    hint: string,
  ): ReactElement {
    return (
      <div className="mb-4 last:mb-0">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span className="font-bold text-wptext-strong">{label}</span>
          {state.is_set ? (
            <>
              <span className="rounded-sm bg-wpnotice-success/15 px-2 py-0.5 text-wptext-strong">
                登録済み（{state.destination_hint}）
              </span>
              <button
                type="button"
                disabled={busy}
                onClick={() => mutation.mutate({ channel, destination: null })}
                className="text-wpnotice-error hover:underline disabled:opacity-50"
              >
                登録を解除
              </button>
            </>
          ) : (
            <span className="rounded-sm bg-wpborder/50 px-2 py-0.5 text-wptext-muted">未登録</span>
          )}
        </div>
        <p className="mb-2 text-xs text-wptext-muted">{hint}</p>
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.trim()) {
              mutation.mutate({ channel, destination: draft.trim(), is_enabled: true });
            }
          }}
        >
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={busy}
            placeholder={placeholder}
            className="min-w-[18rem] rounded-sm border border-wpborder px-3 py-2 disabled:bg-wpcanvas"
          />
          <button
            type="submit"
            disabled={busy || !draft.trim()}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            {state.is_set ? '上書き登録' : '登録する'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <MetaBox title="通知の受け取り方（Slack・Chatwork）">
      <p className="mb-3 text-wptext-muted">
        通知は必ずメールとアプリ内に届きます。ここに宛先を登録すると、
        <strong>同じ内容の要約がSlack・Chatworkにも追加で届きます</strong>
        （登録は任意です。登録した宛先はこの画面に再表示されません）。
      </p>

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      {renderChannel(
        'slack',
        'Slack',
        prefs.slack,
        slackDraft,
        setSlackDraft,
        'https://hooks.slack.com/services/…',
        officeMode
          ? '本人がSlackの「Incoming Webhook」で発行したURLを、依頼を受けて代理登録できます。'
          : 'ご自身のSlackで「Incoming Webhook」を有効にすると発行されるURLを貼り付けてください。通知は発行時に選んだチャンネルに届きます。',
      )}
      {renderChannel(
        'chatwork',
        'Chatwork',
        prefs.chatwork,
        chatworkDraft,
        setChatworkDraft,
        '123456789',
        officeMode
          ? '事務局アカウントとこの受講生の1対1ルームのID（ルームを開いたときのURL末尾 #!rid の後の数字）を登録します。'
          : '事務局とのChatworkの1対1ルームのID（ルームを開いたときのURL末尾 #!rid の後の数字）です。通常は事務局が登録するため、ご自身での入力は不要です。',
      )}
    </MetaBox>
  );
}
